import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { VISA_FAQ_CATEGORIES } from "@/content/visa-corpus/faq-defs";

export const Route = createFileRoute("/en/visa/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/visa-corpus");
    return { faqs: m.VISA_FAQ_EN };
  },
  component: EnVisaFaqHub,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    const title = `Thailand Visa & Immigration FAQ — ${faqs.length} Answers with Official Sources`;
    const description =
      "Thailand visa and immigration FAQ: visa vs permission to stay, TM.7 extensions, 90-day reporting, TM30, retirement, family, DTV, LTR, SMART and work permits — sourced from immigration.go.th, thaievisa.go.th, doe.go.th and boi.go.th.";
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: "/en/visa/faq",
        type: "article",
        locale: "en_US",
        alternates: [{ lang: "th", path: "/visa/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: site },
              { name: "Thailand Visa & Immigration FAQ", url: `${site}/en/visa/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".faq-answer"] },
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/visa/faq")) },
      ],
    };
  },
});

function EnVisaFaqHub() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span>{" "}
          <span>Thailand Visa &amp; Immigration FAQ</span>
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold mb-3">
          Thailand Visa &amp; Immigration FAQ — {faqs.length} Answers
        </h1>
        <p className="text-muted-foreground mb-6">
          Every answer is traceable to the authority that actually decides: the Immigration Bureau
          (immigration.go.th), the Ministry of Foreign Affairs e-Visa system (thaievisa.go.th), the
          Department of Employment (doe.go.th) and the Board of Investment (boi.go.th).
        </p>
        <div className="mb-8 rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
          <strong className="text-foreground">Important:</strong> a visa allows you to travel and request
          entry — it is not a guarantee of admission. Your permitted length of stay comes from the entry stamp
          issued by the immigration officer, and every approval is at the officer's discretion. We do not
          guarantee outcomes.
        </div>

        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>ภาษาไทย:</strong>{" "}
          <a href="/visa/faq" className="text-primary underline">
            FAQ วีซ่าไทยและตรวจคนเข้าเมือง →
          </a>
        </div>

        <section aria-label="Visa FAQ categories" className="mb-8">
          <h2 className="text-lg font-semibold mb-3">Browse by topic</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {VISA_FAQ_CATEGORIES.map((c) => (
              <a
                key={c.slug}
                href={`/en/visa/faq/${c.slug}`}
                className="rounded-lg border bg-card p-3 text-sm hover:border-primary hover:shadow-sm transition"
              >
                <div className="font-semibold mb-1">{c.titleEn}</div>
                <div className="text-xs text-muted-foreground line-clamp-2">{c.descEn}</div>
              </a>
            ))}
          </div>
        </section>

        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4 open:shadow-sm" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="text-muted-foreground mr-2">Q{i + 1}.</span>
                {item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <section className="mt-12">
          <h2 className="text-xl font-bold mb-3">Related services</h2>
          <ul className="grid gap-2 text-sm md:grid-cols-2">
            <li><a className="text-primary underline" href="/en/services/long-term-visas">Long-term visa support</a></li>
            <li><a className="text-primary underline" href="/en/services/work-permit">Work permit &amp; Non-B</a></li>
            <li><a className="text-primary underline" href="/en/translation/faq">Certified translation for visa filings</a></li>
            <li><a className="text-primary underline" href="/en/police-clearance/faq">Police clearance certificate</a></li>
          </ul>
        </section>

        <section className="mt-12 rounded-lg border p-6 bg-primary/5">
          <h2 className="text-xl font-bold mb-2">Still need an answer?</h2>
          <p className="text-sm text-muted-foreground mb-4">Mon–Sat 9:00–18:00 (ICT)</p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 text-primary-foreground font-medium">
            Contact our team →
          </a>
        </section>

        <FaqSourceNote topic="visa" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}